import { DrawerNavigationProp } from "@react-navigation/drawer";
import { MainStackParamList } from "../../navigation/Navigation.types";
import { useAlert, useAppDispatch, useAppSelector } from "@grassrootly/rn-core";
import useBaseRequest from "../../api/BaseRequest";
import { JoinGroup } from "@grassrootly/api/providers/supabase-provider/actions";
import { joinGroupActions } from "../../api/join-group/JoinGroupReducer";
import { groupsActions } from "../../api/groups/GroupsReducer";
import { IGroup, IPlayerGroup } from "@grassrootly/api";
import { useCallback } from "react";

export function useJoinGroup({ navigation }: { navigation: DrawerNavigationProp<MainStackParamList, "join-group"> }) {
  const dispatch = useAppDispatch();
  const playerId = useAppSelector((state) => state.auth.user.id);
  const { success, danger } = useAlert();

  const { loading, execute: joinGroup } = useBaseRequest(JoinGroup, {
    onCompleted: (data) => {
      if (!data) return;

      const groupData: IGroup = {
        id: data.group_id,
        created_at: data.created_at,
        join_code: "",
        group_id: data.group_id,
        is_owner: data.is_owner,
        name: "",
      };
      
      // Also create IPlayerGroup data for the main groups reducer
      const playerGroupData: IPlayerGroup = {
        ...groupData,
        is_owner: data.is_owner,
        is_member: true, // User just joined, so they are a member
      };
      
      dispatch(joinGroupActions.setJoinGroup(groupData));
      // Update the main groups reducer so the group screen recognizes the user as a member
      dispatch(groupsActions.setPlayerGroup(playerGroupData));
      
      success({ message: "Successfully joined the group!" });
      navigation.navigate("group", { id: data.group_id });
    },
    onError: (error) => {
      console.log("Failed to join the group:", error);
      if (error.message === "Group with this join code does not exist.") {
        danger({ message: "Group with this join code does not exist." });
      } else if (error.message === "You are already a member of this group.") {
        danger({ message: "You are already a member of this group." });
      } else {
        danger({ message: error.message || "Failed to join the group. Please try again." });
      }
    },
  });

  const onSubmitJoinGroup = useCallback(
    async (joinCode: string | { join_code: string }) => {
      let code: string;
      if (typeof joinCode === "string") {
        code = joinCode;
      } else {
        code = joinCode.join_code;
      }

      try {
        joinGroup({ join_code: code, playerId });
      } catch (error) {
        console.log("Error executing joinGroup:", error);
        danger({ message: `${error}` || "Failed to join the group. Please try again." });
      }
    },
    [joinGroup, playerId, danger]
  );

  return { onSubmitJoinGroup };
}
